// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { ExistingItem } from '@/api/types';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { ItemDetail } from '@/items/api/types';
import { UpdateItemParameters, updateItem } from '@/items/api/update-item';
import { TakenSkuLink } from '@/items/components/taken-sku-link';
import {
  ITEM_TYPE_ICONS,
  ITEM_TYPE_LABELS,
  type ItemType,
} from '@/items/constants/item-types';
import { nullifyEmptyString } from '@/utilities/nullify-empty-string';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEvent, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  sku: z.string().nullable(),
  description: z.string().nullable(),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  item: ItemDetail;
  onClose: () => void;
}

/**
 * Determines which item types an item can be converted to.
 * Returns an object with each type and whether it's available.
 */
function getAvailableTypeConversions(item: ItemDetail): {
  service: { available: boolean; reason: string | null };
  device: { available: boolean; reason: string | null };
  bulk: { available: boolean; reason: string | null };
} {
  const hasAssets = (item.assetCount ?? 0) > 0;
  const hasInventoryMovements = (item.inventoryMovementCount ?? 0) > 0;

  return {
    service: {
      available:
        item.type === 'SERVICE' ||
        (item.type === 'DEVICE' && !hasAssets) ||
        (item.type === 'BULK' && !hasInventoryMovements),
      reason:
        item.type === 'SERVICE'
          ? null
          : item.type === 'DEVICE' && hasAssets
            ? 'Cannot change type: item has existing assets'
            : item.type === 'BULK' && hasInventoryMovements
              ? 'Cannot change type: item has inventory movements'
              : null,
    },
    device: {
      available: item.type === 'DEVICE' || item.type === 'SERVICE',
      reason:
        item.type === 'DEVICE' || item.type === 'SERVICE'
          ? null
          : 'Bulk items cannot be converted to tracked devices',
    },
    bulk: {
      available:
        item.type === 'BULK' ||
        item.type === 'SERVICE' ||
        (item.type === 'DEVICE' && !hasAssets),
      reason:
        item.type === 'BULK' || item.type === 'SERVICE'
          ? null
          : hasAssets
            ? 'Cannot change type: item has existing assets'
            : null,
    },
  };
}

/**
 * A dialog component for editing existing items.
 *
 * Features:
 * - Form validation using react-hook-form and Zod
 * - Error handling for duplicate names and SKUs
 * - Automatic query invalidation on success
 * - Form state reset on close
 * - Item type conversion with validation
 *
 * Form fields:
 * - Name: Required text input for item name
 * - SKU: Optional text input for stock keeping unit
 * - Description: Optional multiline text input
 * - Type: Radio group for item type (when conversion is allowed)
 */
export function EditItemDialog(props: Props) {
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<ItemType>(props.item.type);
  const [selectedAcceptsBulkSerials, setSelectedAcceptsBulkSerials] =
    useState<boolean>(props.item.shouldRenderSerialFields);
  const [typeError, setTypeError] = useState<string | null>(null);
  const [inlineErrorMessage, setInlineErrorMessage] = useState<string | null>(
    null
  );
  const [showSupportSnackbar, setShowSupportSnackbar] = useState(false);
  const [takenSkuItem, setTakenSkuItem] = useState<ExistingItem | null>(null);

  const typeConversions = getAvailableTypeConversions(props.item);

  const updateItemMutation = useMutation({
    mutationFn: (parameters: UpdateItemParameters) =>
      updateItem(props.item.key, parameters),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item'] });
      props.onClose();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      // Reset both error surfaces; the branches below pick exactly one.
      setInlineErrorMessage(null);
      setShowSupportSnackbar(false);
      if (apiError.errorType === 'UNIQUE_VIOLATION') {
        setError('name', {
          type: 'custom',
          message: 'An item with this name already exists',
        });
        return;
      }
      if (apiError.errorType === 'sku_exists') {
        setTakenSkuItem(apiError.existingItem ?? null);
        setError('sku', {
          type: 'custom',
          message: 'An item with this SKU already exists',
        });
        return;
      }
      // Type-conversion errors. All four codes have curated entries in
      // ERROR_MESSAGES and render on the type radio group.
      const isTypeConversionError =
        apiError.apiStatusCode === ERROR_CODE_MAPPING.product_has_assets ||
        apiError.apiStatusCode ===
          ERROR_CODE_MAPPING.item_has_inventory_movements ||
        apiError.apiStatusCode === ERROR_CODE_MAPPING.item_invalid_type ||
        apiError.apiStatusCode ===
          ERROR_CODE_MAPPING.marked_bulk_to_unmarked_not_allowed;
      if (isTypeConversionError) {
        setTypeError(getUserFacingErrorMessage(apiError));
        return;
      }
      // Other curated codes render inline at the top of the dialog
      // without the misleading "support has been notified" copy.
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setInlineErrorMessage(curated);
        return;
      }
      // Truly unmapped: fire Sentry and surface the SupportSnackbar.
      if (!apiError.sentToSentry) {
        captureException(error);
      }
      setShowSupportSnackbar(true);
    },
  });

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: props.item.name,
      sku: props.item.sku ?? null,
      description: props.item.description ?? null,
    },
  });

  const { control, formState, reset, setError, handleSubmit, watch } = methods;

  const currentDescription = watch('description');
  const currentSku = watch('sku');

  // Normalize values once for comparison and submission
  const normalizedDescription = nullifyEmptyString(currentDescription);
  const normalizedSku = nullifyEmptyString(currentSku);

  const descriptionChanged = normalizedDescription !== props.item.description;
  const skuChanged = normalizedSku !== props.item.sku;
  const typeChanged = selectedType !== props.item.type;
  const acceptsBulkSerialsChanged =
    selectedType === 'BULK' &&
    selectedAcceptsBulkSerials !== props.item.shouldRenderSerialFields;

  // Compute actual dirty state considering "" and null as equivalent for SKU and description
  const isDirty =
    formState.dirtyFields.name ||
    (formState.dirtyFields.description && descriptionChanged) ||
    (formState.dirtyFields.sku && skuChanged) ||
    typeChanged ||
    acceptsBulkSerialsChanged;

  const close = () => {
    props.onClose();
    reset();
    setSelectedType(props.item.type);
    setSelectedAcceptsBulkSerials(props.item.shouldRenderSerialFields);
    setTypeError(null);
    setInlineErrorMessage(null);
    setShowSupportSnackbar(false);
    setTakenSkuItem(null);
  };

  const handleTypeChange = (_event: MouseEvent, newType: ItemType | null) => {
    if (newType !== null) {
      setSelectedType(newType);
      setTypeError(null);
    }
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    const parameters: UpdateItemParameters = {};
    if (formState.dirtyFields.name) parameters.name = data.name;
    if (formState.dirtyFields.description && descriptionChanged) {
      parameters.description = normalizedDescription;
    }
    if (formState.dirtyFields.sku && skuChanged) {
      parameters.sku = normalizedSku;
    }

    // Type changes also cover the serial acceptance toggle on a bulk item.
    if (typeChanged || acceptsBulkSerialsChanged) {
      parameters.type = selectedType;
      parameters.acceptsBulkSerials = selectedAcceptsBulkSerials;
    }

    if (Object.entries(parameters).length > 0) {
      setTakenSkuItem(null);
      updateItemMutation.mutate(parameters);
    }
  };

  // Read every render, so react-hook-form subscribes to the SKU error.
  const hasSkuError = formState.errors.sku !== undefined;

  // Always show type conversion UI so users can see their current type
  // and understand why conversions may not be available
  const showTypeConversion = true;

  return (
    <FormProvider {...methods}>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <SupportSnackbar
          isOpen={showSupportSnackbar}
          message="An error occurred while updating the item."
          onClose={() => {
            setShowSupportSnackbar(false);
            updateItemMutation.reset();
          }}
        />
        <DialogTitle>Edit item</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {inlineErrorMessage && (
              <Alert
                severity="error"
                onClose={() => setInlineErrorMessage(null)}
              >
                {inlineErrorMessage}
              </Alert>
            )}
            <FormTextField name="name" label="Name" control={control} />
            <Stack spacing={0.5}>
              <FormTextField name="sku" label="SKU" control={control} />
              {takenSkuItem !== null && hasSkuError && (
                <TakenSkuLink existingItem={takenSkuItem} />
              )}
            </Stack>
            <FormTextField
              name="description"
              label="Description"
              control={control}
              isMultiline
              minimumRows={2}
            />
            {showTypeConversion && (
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Item type
                </Typography>
                <ToggleButtonGroup
                  value={selectedType}
                  exclusive
                  onChange={handleTypeChange}
                  fullWidth
                  size="small"
                >
                  {(['SERVICE', 'DEVICE', 'BULK'] as const).map((type) => {
                    const Icon = ITEM_TYPE_ICONS[type];
                    const conversion =
                      typeConversions[
                        type.toLowerCase() as 'service' | 'device' | 'bulk'
                      ];
                    const isDisabled = !conversion.available;
                    const tooltipTitle = conversion.reason ?? '';

                    return (
                      <Tooltip key={type} title={tooltipTitle} arrow>
                        <span style={{ display: 'flex', flex: 1 }}>
                          <ToggleButton
                            value={type}
                            disabled={isDisabled}
                            sx={{
                              textTransform: 'none',
                              gap: 0.5,
                              flex: 1,
                            }}
                          >
                            <Icon fontSize="small" />
                            {ITEM_TYPE_LABELS[type]}
                          </ToggleButton>
                        </span>
                      </Tooltip>
                    );
                  })}
                </ToggleButtonGroup>
                {typeError && (
                  <Typography variant="caption" color="error">
                    {typeError}
                  </Typography>
                )}
              </Stack>
            )}
            <Divider />
            {selectedType === 'BULK' && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Allow users to enter serials for this item
                  </Typography>
                  <Tooltip
                    arrow
                    placement="right"
                    title="When allocating units of this part, such as during assembly, users will have the option to manually enter serial numbers for future reference (this does not make the part units into serialized assets)"
                  >
                    <InfoOutlined
                      fontSize="small"
                      color="action"
                      sx={{ fontSize: 16 }}
                    />
                  </Tooltip>
                </Stack>
                <Switch
                  checked={selectedAcceptsBulkSerials}
                  size="small"
                  onChange={(_, checked) => {
                    setSelectedAcceptsBulkSerials(checked);
                    setTypeError(null);
                  }}
                />
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={close}>
            Cancel
          </Button>
          <Button
            size="small"
            type="submit"
            color="secondary"
            variant="contained"
            disabled={!isDirty}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
